import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import Swal from "sweetalert2";
import { Button, Form, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

import { url } from "../../../../store/initialStatus";
import "./EnrollmentCharter.css";

interface CharterStudent {
  id: number;
  fullname: string;
}

interface CharterStatus {
  charter_version: string;
  guardian: {
    name: string;
    national_id: string;
  };
  pending_students: CharterStudent[];
  requires_signature: boolean;
}

/** Escapes a value before it is injected into the charter HTML template. */
const escapeHtml = (value: string): string =>
  value.replace(/[&<>'"]/g, (character) => {
    const entities: { [key: string]: string } = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "'": "&#039;",
      '"': "&quot;",
    };
    return entities[character];
  });

/**
 * Mandatory Enrollment Charter & Family Agreement.
 *
 * Opens a blocking modal while the backend reports `requires_signature`.
 * The agreement text comes from public/enrollment-charter-v3.html with the
 * guardian's name and national id filled in, rendered in a sandboxed iframe.
 */
const EnrollmentCharter = () => {
  const signaturePad = useRef<SignatureCanvas | null>(null);
  const [status, setStatus] = useState<CharterStatus | null>(null);
  const [template, setTemplate] = useState("");
  const [confirmed, setConfirmed] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadStatus = async () => {
    try {
      const response = await axios.post(`${url}guardian/getenrollmentcharter.php`);
      setStatus(response.data.data);
    } catch (error: any) {
      setStatus(null);
      Swal.fire(
        "Unable to load the Enrollment Charter",
        error?.response?.data?.msg || "Please refresh the page and try again.",
        "error"
      );
    }
  };

  useEffect(() => {
    loadStatus();
    fetch(`${process.env.PUBLIC_URL}/enrollment-charter-v3.html`)
      .then((response) => {
        if (!response.ok) throw new Error("Charter template could not be loaded.");
        return response.text();
      })
      .then(setTemplate)
      .catch(() => {
        Swal.fire("Unable to load the agreement", "Please refresh the page and try again.", "error");
      });
  }, []);

  const charterHtml = useMemo(() => {
    if (!template || !status) return "";
    return template
      .replace(/\{\{GUARDIAN_NAME\}\}/g, escapeHtml(status.guardian.name || ""))
      .replace(/\{\{NATIONAL_ID\}\}/g, escapeHtml(status.guardian.national_id || ""));
  }, [status, template]);

  const clearSignature = () => signaturePad.current?.clear();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!confirmed) {
      Swal.fire("Confirmation required", "Please confirm that you agree to the charter.", "warning");
      return;
    }
    if (!signaturePad.current || signaturePad.current.isEmpty()) {
      Swal.fire("Signature required", "Please sign before submitting.", "warning");
      return;
    }

    setSubmitting(true);
    Swal.showLoading();
    try {
      const signatureDataUrl = signaturePad.current.getTrimmedCanvas().toDataURL("image/png");
      const response = await axios.post(`${url}guardian/saveenrollmentcharter.php`, {
        confirmed: true,
        signature_dataurl: signatureDataUrl,
      });
      await Swal.fire(
        "Enrollment Charter saved",
        "",
        "success"
      );
      setConfirmed(false);
      clearSignature();
      await loadStatus();
    } catch (error: any) {
      Swal.fire(
        "Unable to save the Enrollment Charter",
        error?.response?.data?.msg || "Please try again.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isOpen = Boolean(status?.requires_signature);

  return (
    <Modal
      isOpen={isOpen}
      backdrop="static"
      keyboard={false}
      size="xl"
      centered
      contentClassName="enrollment-charter-modal"
      labelledBy="enrollment-charter-title"
    >
      <ModalHeader id="enrollment-charter-title">
        Enrollment Charter &amp; Family Agreement
      </ModalHeader>
      <Form onSubmit={submit} className="enrollment-charter-form">
        <ModalBody className="enrollment-charter-body">
          <div className="enrollment-charter-students" aria-live="polite">
            <strong>This agreement covers:</strong>{" "}
            {status?.pending_students.map((student) => student.fullname).join(", ")}
          </div>
          {charterHtml ? (
            <iframe
              className="enrollment-charter-document"
              title="IVY Enrollment Charter bilingual agreement"
              sandbox=""
              srcDoc={charterHtml}
            />
          ) : (
            <div className="enrollment-charter-loading">Loading agreement…</div>
          )}
        </ModalBody>
        <ModalFooter className="enrollment-charter-footer">
          <div className="enrollment-charter-confirmation">
            <Label check>
              <Input
                type="checkbox"
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
                disabled={submitting}
                required
              />{" "}
              I have read, understood, and agree to the Enrollment Charter on behalf of the students listed above.
            </Label>
          </div>
          <div className="enrollment-charter-signature-wrap">
            <span className="enrollment-charter-signature-label">Signature <span className="text-danger">*</span></span>
            <SignatureCanvas
              ref={signaturePad}
              canvasProps={{
                className: "enrollment-charter-signature",
                "aria-label": "Guardian signature area",
              }}
            />
          </div>
          <div className="enrollment-charter-actions">
            <Button type="button" color="warning" onClick={clearSignature} disabled={submitting}>
              Clear
            </Button>
            <Button type="submit" color="primary" disabled={submitting || !template}>
              {submitting ? "Submitting…" : "Sign and submit"}
            </Button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default EnrollmentCharter;
